/**
 * Egyszerű SVG-alapú chartok — nincs külső dependency (recharts/d3 sincs).
 * Csak amit az alkalmazás aktuálisan igényel:
 *  - BarChart: oszlopdiagram (havi költés trend)
 *  - DonutChart: szegmens-arányos donut (kategória bontás)
 */
import { useMemo } from 'react'

interface BarPoint {
  label: string
  value: number
}

/** Egyszerű függőleges oszlopdiagram. */
export function BarChart({
  data,
  height = 200,
  format = (v: number) => v.toLocaleString('hu-HU'),
  emptyHint = 'Nincs adat',
}: {
  data: BarPoint[]
  height?: number
  format?: (v: number) => string
  emptyHint?: string
}) {
  const max = useMemo(() => Math.max(1, ...data.map((d) => d.value)), [data])
  if (data.length === 0 || max === 0) {
    return (
      <div className="h-40 grid place-items-center text-sm text-slate-400 dark:text-slate-500">
        {emptyHint}
      </div>
    )
  }
  const W = 800
  const H = height
  const padding = { top: 12, right: 16, bottom: 32, left: 56 }
  const innerW = W - padding.left - padding.right
  const innerH = H - padding.top - padding.bottom
  const barW = innerW / data.length
  const barInner = Math.max(4, barW * 0.7)
  const ticks = niceTicks(max, 4)

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-auto"
        role="img"
      >
        {/* Y axis ticks */}
        {ticks.map((t, i) => {
          const y = padding.top + innerH - (t / ticks[ticks.length - 1]) * innerH
          return (
            <g key={i}>
              <line x1={padding.left} x2={W - padding.right} y1={y} y2={y} stroke="currentColor" className="text-slate-200 dark:text-slate-700" strokeDasharray="2,3" />
              <text x={padding.left - 6} y={y + 4} fontSize="10" textAnchor="end" className="fill-slate-500 dark:fill-slate-400">
                {format(t)}
              </text>
            </g>
          )
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const x = padding.left + i * barW + (barW - barInner) / 2
          const h = (d.value / max) * innerH
          const y = padding.top + innerH - h
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barInner}
                height={Math.max(1, h)}
                rx={3}
                className="fill-brand-500 hover:fill-brand-600 dark:fill-brand-400"
              >
                <title>{`${d.label}: ${format(d.value)}`}</title>
              </rect>
              <text
                x={x + barInner / 2}
                y={H - padding.bottom + 14}
                fontSize="10"
                textAnchor="middle"
                className="fill-slate-600 dark:fill-slate-300"
              >
                {d.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

function niceTicks(max: number, count: number): number[] {
  if (max <= 0) return [0]
  const step = niceStep(max / count)
  const out: number[] = []
  let v = 0
  while (v <= max + step / 2) {
    out.push(v)
    v += step
  }
  return out
}

function niceStep(raw: number): number {
  const exp = Math.floor(Math.log10(raw))
  const f = raw / Math.pow(10, exp)
  let nf
  if (f < 1.5) nf = 1
  else if (f < 3) nf = 2
  else if (f < 7) nf = 5
  else nf = 10
  return nf * Math.pow(10, exp)
}

interface DonutSlice {
  label: string
  value: number
  color: string
}

/** Pie/donut chart svg-vel. */
export function DonutChart({
  data,
  size = 200,
  inner = 0.55,
  emptyHint = 'Nincs adat',
}: {
  data: DonutSlice[]
  size?: number
  inner?: number
  emptyHint?: string
}) {
  const total = data.reduce((a, b) => a + b.value, 0)
  if (data.length === 0 || total === 0) {
    return (
      <div className="grid place-items-center text-sm text-slate-400 dark:text-slate-500" style={{ height: size }}>
        {emptyHint}
      </div>
    )
  }
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 4
  const ri = r * inner
  let angle = -Math.PI / 2 // 12 óra állásból indulunk
  const segments = data.map((s) => {
    const frac = s.value / total
    const startAngle = angle
    const endAngle = angle + frac * Math.PI * 2
    angle = endAngle
    return { ...s, startAngle, endAngle, frac }
  })
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        {segments.map((s, i) => {
          const path = arcPath(cx, cy, r, ri, s.startAngle, s.endAngle)
          return (
            <path key={i} d={path} fill={s.color} stroke="white" strokeWidth="1">
              <title>{`${s.label}: ${Math.round(s.value).toLocaleString('hu-HU')} Ft (${Math.round(s.frac * 100)}%)`}</title>
            </path>
          )
        })}
      </svg>
      <ul className="space-y-1 text-xs">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm" style={{ background: s.color }} />
            <span className="text-slate-700 dark:text-slate-200 max-w-[12rem] truncate">{s.label}</span>
            <span className="text-slate-500 dark:text-slate-400 tabular-nums">
              · {Math.round(s.value).toLocaleString('hu-HU')} ({Math.round(s.frac * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function arcPath(cx: number, cy: number, r: number, ri: number, start: number, end: number): string {
  // Donut szegmens path az outer arc → straight → inner arc (reverse) → close
  const largeArc = end - start > Math.PI ? 1 : 0
  const x0 = cx + r * Math.cos(start)
  const y0 = cy + r * Math.sin(start)
  const x1 = cx + r * Math.cos(end)
  const y1 = cy + r * Math.sin(end)
  const xi0 = cx + ri * Math.cos(end)
  const yi0 = cy + ri * Math.sin(end)
  const xi1 = cx + ri * Math.cos(start)
  const yi1 = cy + ri * Math.sin(start)
  return [
    `M ${x0} ${y0}`,
    `A ${r} ${r} 0 ${largeArc} 1 ${x1} ${y1}`,
    `L ${xi0} ${yi0}`,
    `A ${ri} ${ri} 0 ${largeArc} 0 ${xi1} ${yi1}`,
    'Z',
  ].join(' ')
}
